import "./styles.scss";

import { FaPlus } from "react-icons/fa";
import { FaCheck } from "react-icons/fa6";
import { IoIosClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getStudentPlansUser,
  createContentExercises,
  getAvailableTopicsByLevel,
  getSuggestedTopicsByTeacher,
  getLastLanguageOfFormStudent,
  getCurrentCycleAndLevelByStudentId,
} from "../../store/middleware";
import useIsMobile from "../../hook/useIsMobile";
import aiIcon from "../../assets/images/ai-icon.png";
import ModalMyHistory from "../exerciseIA/ModalMyHistory";
import SelectLanguages from "../classDetails/SelectLanguages";
import ModalTopicsStudy from "../exerciseIA/ModalTopicsStudy";
import { endLoading, initLoading } from "../../store/slices/generics.slice";
import { clearContentExercises } from "../../store/slices/contentExercises.slice";
import ModalGenerateExercisesWithAI from "../exerciseIA/ModalGenerateExercisesWithAI";

export const MAX_SELECTABLE_TOPICS = 2;

const languageIdByFormatted = { EN: 1, ES: 2, PT: 3, FR: 4, "CS-US-IN": 101 };
const languageFormattedId = { 1: "EN", 2: "ES", 3: "PT", 4: "FR", 101: "CS-US-IN" };

const HomeStudentStudyAI = ({ student }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const { user } = useSelector((state) => state.userReducer);
  const { plans } = useSelector((state) => state.studentReducer);
  const { availableTopics, suggestedTopicsByTeachers } = useSelector((state) => state.contentExercisesReducer);

  const [language, setLanguage] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [allowedLanguage, setAllowedLanguage] = useState([]);
  const [currentCycleAndLevel, setCurrentCycleAndLevel] = useState([]);
  const [modalTopicsSource, setModalTopicsSource] = useState("available");
  const [selectedTopicsteachers, setSelectedTopicsteachers] = useState([]);

  const [showModalMyHistory, setShowModalMyHistory] = useState(false);
  const [showModalTopicsStudy, setShowModalTopicsStudy] = useState(false);
  const [showModalGenerateExercisesWithAI, setShowModalGenerateExercisesWithAI] = useState(false);

  const userId = user?.role === "student" ? user?.id : student?.userId;
  const studentId = user?.role === "student" ? user?.studentId : student?.id;

  const MAX_TOPICS_TO_SHOW = isMobile ? 2 : 3;
  const allSelectedTopics = [...selectedTopics, ...selectedTopicsteachers];
  const availableTopicsCard = availableTopics?.[0]?.suggestedContents ?? [];
  const totalSelectedTopicsCount = selectedTopics.length + selectedTopicsteachers.length;
  const teacherSuggestedTopicsCard = suggestedTopicsByTeachers?.[0]?.suggestedContents ?? [];
  const shouldShowEmptyState = !availableTopicsCard.length && !teacherSuggestedTopicsCard.length;
  const modalSelectedTopics = modalTopicsSource === "teacher" ? selectedTopicsteachers : selectedTopics;
  const modalAvailableTopics = modalTopicsSource === "teacher" ? suggestedTopicsByTeachers : availableTopics;

  const availableTopicsAll = availableTopics
    ?.flatMap((item) => item?.suggestedContents ?? [])
    ?.filter((item, index, self) => self.findIndex((topic) => topic.id === item.id) === index);

  const teacherSuggestedTopicsAll = suggestedTopicsByTeachers
    ?.flatMap((item) => item?.suggestedContents ?? [])
    ?.filter((item, index, self) => self.findIndex((topic) => topic.id === item.id) === index);

  const modalSelectedTopicsWithGlobalLimit =
    modalTopicsSource === "teacher"
      ? [...selectedTopicsteachers, ...selectedTopics]
      : [...selectedTopics, ...selectedTopicsteachers];

  const handleOnclickOptions = (value) => {
    if (value === language) return;
    setLanguage(value);
  };

  const handleOpenModalMyHistory = () => setShowModalMyHistory(true);

  const handleOpenModalGenerateExercisesWithAI = () => setShowModalGenerateExercisesWithAI(true);

  const handleOpenModalTopicsStudy = (source) => {
    setModalTopicsSource(source);
    setShowModalTopicsStudy(true);
  };

  const resolveAllowedLanguages = async (user, plans, userId) => {
    if (user?.role === "student") {
      return plans?.flatMap((item) => item.studentPlanLanguages.map((lang) => lang.lingoLanguage.id)) ?? [];
    }

    const studentPlans = await dispatch(getStudentPlansUser(userId));
    return studentPlans?.flatMap((item) => item.studentPlanLanguages.map((lang) => lang.lingoLanguage.id)) ?? [];
  };

  const resolveCurrentLanguageId = (data, allowedLanguages) => {
    return (
      data?.currentLanguage?.[0]?.lingoLanguageId ??
      data?.lastDoneLanguage?.[0]?.lingoLanguageId ??
      allowedLanguages[0] ??
      null
    );
  };

  const loadTopicsByLanguage = async (languageCode) => {
    const selectedLanguageId = languageIdByFormatted[languageCode];
    if (!selectedLanguageId || !studentId) return;

    try {
      dispatch(initLoading());
      setSelectedTopics([]);
      setSelectedTopicsteachers([]);
      dispatch(clearContentExercises());

      const cycleLevel = await dispatch(
        getCurrentCycleAndLevelByStudentId({ studentId, language: selectedLanguageId }),
      );

      if (!cycleLevel?.length) return;
      setCurrentCycleAndLevel(cycleLevel);

      const topicsPayload = {
        studentId,
        lingoLanguageId: selectedLanguageId,
        currentLevel: cycleLevel?.[0]?.level,
        currentCycle: cycleLevel?.[0]?.contentCycle ?? 1,
      };

      await Promise.all([
        dispatch(getAvailableTopicsByLevel(topicsPayload)),
        dispatch(getSuggestedTopicsByTeacher(topicsPayload)),
      ]);
    } catch (err) {
      console.error("[loadTopicsByLanguage]", err);
    } finally {
      dispatch(endLoading());
    }
  };

  const getData = async () => {
    try {
      dispatch(initLoading());

      const [allowedLanguages, lastLanguageData] = await Promise.all([
        resolveAllowedLanguages(user, plans, userId),
        dispatch(getLastLanguageOfFormStudent({ type: "student", studentId })),
      ]);

      setAllowedLanguage(allowedLanguages);

      const currentLanguageId = resolveCurrentLanguageId(lastLanguageData, allowedLanguages);
      setLanguage(languageFormattedId[currentLanguageId]);
    } catch (err) {
      console.error("[getData]", err);
    } finally {
      dispatch(endLoading());
    }
  };

  const handleSelectTopic = (id) => {
    const selectedTopic = availableTopicsAll.find((item) => item.id === id);
    if (!selectedTopic) return;

    setSelectedTopics((prev) => {
      if (prev.some((p) => p.id === selectedTopic.id)) return prev;
      if (prev.length + selectedTopicsteachers.length >= MAX_SELECTABLE_TOPICS) return prev;
      return [...prev, selectedTopic];
    });
  };

  const handleSelectTopicTeacher = (id) => {
    const selectedTopic = teacherSuggestedTopicsAll.find((item) => item.id === id);
    if (!selectedTopic) return;

    setSelectedTopicsteachers((prev) => {
      if (prev.some((item) => item.id === selectedTopic.id)) return prev;
      if (prev.length + selectedTopics.length >= MAX_SELECTABLE_TOPICS) return prev;
      return [...prev, selectedTopic];
    });
  };

  const handleUnselectTopic = (id) => {
    setSelectedTopics((prev) => prev.filter((t) => t.id !== id));
  };

  const handleUnselectTopicTeacher = (id) => {
    setSelectedTopicsteachers((prev) => prev.filter((t) => t.id !== id));
  };

  const modalOnSelectTopic = modalTopicsSource === "teacher" ? handleSelectTopicTeacher : handleSelectTopic;
  const modalOnUnselectTopic = modalTopicsSource === "teacher" ? handleUnselectTopicTeacher : handleUnselectTopic;

  const handleGenerateExercises = async () => {
    const payload = {
      userId,
      language,
      difficulty: "medium",
      level: currentCycleAndLevel[0]?.level,
      contentTopics: selectedTopics.map((topic) => ({ contentCode: topic.contentCode })),
      suggestByTeacherTopics: selectedTopicsteachers.map((topic) => ({ contentCode: topic.contentCode })),
    };
    await dispatch(createContentExercises(payload));
    handleOpenModalMyHistory();
    setSelectedTopics([]);
    setSelectedTopicsteachers([]);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!language) return;
    loadTopicsByLanguage(language);
    // eslint-disable-next-line
  }, [language]);

  return (
    <div className="containerHomeStudentStudyAI">
      <SelectLanguages language={language} allowedLanguage={allowedLanguage} onClickOptions={handleOnclickOptions} />

      <div className="homeStudentStudyAIHeader">
        <div className="aiIconWrapper">
          <img src={aiIcon} alt="AI" className="aiIcon" />
        </div>

        <div className="headerTexts">
          <h2 className="title">{t("AI_PRACTICE_TITLE")}</h2>
          {shouldShowEmptyState ? (
            <h3 className="subtitle">{t("AI_PRACTICE_EMPTY_TITLE")}</h3>
          ) : (
            <Fragment>
              <p className="subtitle">{t("AI_PRACTICE_DESCRIPTION")}</p>
              <p className="subtitle">{t("AI_PRACTICE_LIST_DESCRIPTION", { count: MAX_SELECTABLE_TOPICS })}</p>
            </Fragment>
          )}
        </div>
      </div>

      <div className="wrapperTopicsSection">
        {availableTopicsCard.length > 0 && (
          <div className="topicsSection">
            <h3 className="topicsTitle">{t("AI_PRACTICE_TOPICS_AVAILABLE")}</h3>

            <div className="topicsBox">
              <div className="topicsTopRow">
                <div className="topicsChipsRow">
                  {availableTopicsCard
                    ?.filter((item) => !selectedTopics.some(({ id }) => id === item.id))
                    ?.slice(0, MAX_TOPICS_TO_SHOW)
                    ?.map(({ id, classTitle }) => (
                      <button
                        key={id}
                        type="button"
                        className="topicChip"
                        onClick={() => handleSelectTopic(id)}
                        disabled={totalSelectedTopicsCount >= MAX_SELECTABLE_TOPICS}
                      >
                        <div className="circle" />
                        <span className="chipLabel">{classTitle}</span>
                      </button>
                    ))}
                </div>

                <button
                  className="buttonPlus"
                  disabled={totalSelectedTopicsCount >= MAX_SELECTABLE_TOPICS}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModalTopicsStudy("available");
                  }}
                >
                  <FaPlus color="#004fff" size={14} />
                </button>
              </div>

              <div className="topicsBottomRow">
                {selectedTopics.length ? (
                  <div className="topicsSelectedRow">
                    {selectedTopics.map(({ id, classTitle }) => (
                      <button
                        key={id}
                        type="button"
                        className="topicChip topicChipSelected"
                        onClick={() => handleUnselectTopic(id)}
                      >
                        <FaCheck color="#fff" size={18} />
                        <span className="chipLabel">{classTitle}</span>
                        <IoIosClose color="#fff" size={24} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="topicsPlaceholder">{t("AI_PRACTICE_SELECT_TOPICS")}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {teacherSuggestedTopicsCard.length > 0 && (
          <div className="topicsSection">
            <h3 className="topicsTitle">{t("AI_PRACTICE_TEACHER_RECOMMEND")}</h3>

            <div className="topicsBox">
              <div className="topicsTopRow">
                <div className="topicsChipsRow">
                  {teacherSuggestedTopicsCard
                    ?.filter((t) => !selectedTopicsteachers.some(({ id }) => id === t.id))
                    ?.slice(0, MAX_TOPICS_TO_SHOW)
                    ?.map(({ id, classTitle }) => (
                      <button
                        key={id}
                        type="button"
                        className="topicChip"
                        disabled={totalSelectedTopicsCount >= MAX_SELECTABLE_TOPICS}
                        onClick={() => handleSelectTopicTeacher(id)}
                      >
                        <div className="circle" />
                        <span className="chipLabel">{classTitle}</span>
                      </button>
                    ))}
                </div>

                <button
                  className="buttonPlus"
                  disabled={totalSelectedTopicsCount >= MAX_SELECTABLE_TOPICS}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModalTopicsStudy("teacher");
                  }}
                >
                  <FaPlus color="#004fff" size={16} />
                </button>
              </div>

              <div className="topicsBottomRow">
                {selectedTopicsteachers.length ? (
                  <div className="topicsSelectedRow">
                    {selectedTopicsteachers.map(({ id, classTitle }) => (
                      <button
                        key={id}
                        type="button"
                        className="topicChip topicChipSelected"
                        onClick={() => handleUnselectTopicTeacher(id)}
                      >
                        <FaCheck color="#fff" size={18} />
                        <span className="chipLabel">{classTitle}</span>
                        <IoIosClose color="#fff" size={24} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="topicsPlaceholder">{t("AI_PRACTICE_SELECT_TOPICS")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="footer">
        <button className="historyButton" onClick={handleOpenModalMyHistory} disabled={shouldShowEmptyState}>
          {t("AI_PRACTICE_MY_HISTORY")}
        </button>
        {user?.role === "student" && (
          <button className="submitButton" onClick={handleGenerateExercises} disabled={totalSelectedTopicsCount === 0}>
            {t("AI_PRACTICE_GENERATE")}
          </button>
        )}
      </div>

      {showModalTopicsStudy && (
        <ModalTopicsStudy
          showModal={showModalTopicsStudy}
          onSelectTopic={modalOnSelectTopic}
          selectedTopics={modalSelectedTopics}
          modalTopicsSource={modalTopicsSource}
          availableTopics={modalAvailableTopics}
          onUnselectTopic={modalOnUnselectTopic}
          setShowModal={setShowModalTopicsStudy}
          selectedTopicsForLimit={modalSelectedTopicsWithGlobalLimit}
        />
      )}

      {showModalGenerateExercisesWithAI && (
        <ModalGenerateExercisesWithAI
          selectedTopics={allSelectedTopics}
          showModal={showModalGenerateExercisesWithAI}
          setShowModal={setShowModalGenerateExercisesWithAI}
        />
      )}

      {showModalMyHistory && (
        <ModalMyHistory
          userId={userId}
          language={language}
          showModal={showModalMyHistory}
          setShowModal={setShowModalMyHistory}
          handleOpenModalGenerateExercisesWithAI={handleOpenModalGenerateExercisesWithAI}
        />
      )}
    </div>
  );
};

export default HomeStudentStudyAI;
